import { ArrowRight, ArrowUpRight } from "lucide-react";

const IMG = "/assets/fin-reco-demo.png";
const STAGES = ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"];

const Thumb = () => (
  <div className="group block relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
    </div>
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#211307]/85 via-[#211307]/45 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Finance Close</p>
          <p className="text-[15px] font-semibold text-white mt-0.5">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-white text-[#211307] flex items-center justify-center shrink-0"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </div>
  </div>
);

/* Pills adapt to dark/light block */
const Pills = ({ dark }) => (
  <div className="flex flex-wrap items-center gap-2.5">
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center gap-1.5">
        <span className={`flex items-center gap-2 rounded-full px-3.5 py-2 ${dark ? "bg-white/8" : "bg-[#211307]/6"}`}>
          <span className="font-mono2 text-[10px] text-[#B87A32]">{String(i + 1).padStart(2, "0")}</span>
          <span className={`text-[12.5px] font-semibold ${dark ? "text-white" : "text-[#3E2818]"}`}>{s}</span>
        </span>
        {i < STAGES.length - 1 && <ArrowRight size={14} className="text-[#B87A32]" />}
      </div>
    ))}
  </div>
);

const Block = ({ className, dark, labelColor, textColor }) => (
  <div className={`mt-1 rounded-2xl p-5 lg:p-6 ${className}`}>
    <h4 className={`font-display text-xl font-medium ${dark ? "text-[#F7F5F0]" : "text-[#3E2818]"}`}>Hire-to-Retire</h4>
    <p className={`text-[13px] mt-2 mb-5 max-w-2xl leading-relaxed ${textColor}`}>Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.</p>
    <Pills dark={dark} />
  </div>
);

/* Variants */
const A = () => <Block dark className="bg-gradient-to-br from-[#7A4A28] to-[#4A2C15]" textColor="text-white/75" />;
const B = () => <Block className="bg-[#FBF2E1] border border-[#8A5730]/20" textColor="text-[#3E2818]/65" />;
const C = () => <Block dark className="bg-gradient-to-br from-[#2E1B0C] to-[#5A3418]" textColor="text-white/70" />;
const D = () => <Block className="bg-[#F1E7D6] border border-[#8A5730]/15" textColor="text-[#3E2818]/65" />;
const E = () => <Block dark className="bg-[#211307] ring-1 ring-inset ring-[#E8AA68]/25 border-l-4 border-[#EDA13A]" textColor="text-white/70" />;
const F = () => <Block className="bg-white border border-[#3E2818]/12 shadow-[0_20px_50px_-34px_rgba(62,40,24,0.5)]" textColor="text-[#3E2818]/65" />;

const OPTIONS = [
  { id: "a", title: "Option A · Warm Caramel Gradient", note: "Warm brown gradient (caramel → deep cocoa). Clearly warmer than the espresso thumbnail, so the two no longer read as the same block.", comp: A },
  { id: "b", title: "Option B · Cream-Gold Card (light)", note: "Flip the block to light cream-gold with dark text. Strong contrast against the dark thumbnail bottom — feels airy and premium.", comp: B },
  { id: "c", title: "Option C · Espresso → Caramel Gradient", note: "Keeps it dark but adds a warm gradient with a lighter caramel edge, giving it its own identity vs the flat thumbnail overlay.", comp: C },
  { id: "d", title: "Option D · Soft Oat Card (light)", note: "A light warm-oat card with a subtle border. Neutral and calm, letting the thumbnail be the only dark element.", comp: D },
  { id: "e", title: "Option E · Espresso + Gold Accent", note: "Stays espresso but adds a gold left-bar and inset ring so it reads as a distinct, framed panel rather than a flat dark rectangle.", comp: E },
  { id: "f", title: "Option F · Clean White Card", note: "A plain white card with soft shadow. Maximum separation — the thumbnail becomes the single focal dark element.", comp: F },
];

export default function HireBlockOptions() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Hire-to-Retire Block · Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a background for the use-case block</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Each option pairs the Hire-to-Retire block with the actual demo thumbnail so you can see how they sit together. Tell me the letter and I&rsquo;ll apply it.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-start">
                  <C />
                  <Thumb />
                </div>
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
